import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import SubmitButton from "../../common/SubmitButton";
import http from "../../../helpers/http";
import { doObjToFormData } from "../../../helpers/helpers";
import { fetchSiteSettings } from "../../../states/actions/fetchSiteSettings";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  CardElement,
} from "@stripe/react-stripe-js";

const CSS_STYLES = `
.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
`;

const useOptions = () => {
  const options = useMemo(
    () => ({
      hidePostalCode: true,
      style: {
        base: {
          display: "block",
          width: "100%",
          height: "5.3rem",
          fontFamily: "'Red Hat Display', sans-serif",
          fontWeight: "500",
          color: "#061237",
          background: "#fff",
          "text-align": "left",
          padding: "0.6rem 1.4rem",
          "::placeholder": {
            color: "#130a2952",
            fontSize: "15px",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    }),
    []
  );

  return options;
};
export default function UpdateCardDetails({ setCardPopup, card_details }) {
  const options = useOptions();
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const dispatch = useDispatch();
  const {
    register,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm();
  const watchAllFields = watch();
  const handleCardDetailsChange = (ev) => {
    ev.error ? setCardError(ev.error.message) : setCardError();
  };
  // Saves the card on the customer's existing subscription and pays the
  // invoice that failed on the old one, so a declined renewal is fixed in
  // place rather than by buying the plan again.
  const onSubmit = async (data, e) => {
    e?.preventDefault();
    if (!stripe || !elements || isFormProcessing) return;

    setIsFormProcessing(true);
    setCardError(null);
    setInvoiceUrl(null);
    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: { name: data.card_holder_name },
      });
      if (error) {
        setCardError(error.message);
        return;
      }

      const { data: res } = await http.post(
        "update-payment-method",
        doObjToFormData({
          payment_method: paymentMethod.id,
          name_on_card: data.card_holder_name,
        })
      );

      if (res?.status === 1) {
        toast.success(res.msg);
        // Access comes back once Stripe confirms the payment (webhook), so
        // reload the member a moment later rather than immediately.
        setTimeout(() => dispatch(fetchSiteSettings()), res.paid_invoices > 0 ? 4000 : 0);
        setCardPopup(false);
        return;
      }

      setCardError(res?.msg || "Your card could not be updated. Please try again.");
      if (res?.invoice_url) setInvoiceUrl(res.invoice_url);
    } catch (err) {
      setCardError("Your card could not be updated. Please try again.");
    } finally {
      setIsFormProcessing(false);
    }
  };

  return (
    <>
      <div className="popup lg active lg">
        <div className="table_dv">
          <div className="table_cell">
            <div className="_inner">
              <div className="x_btn" onClick={() => setCardPopup(false)}></div>
              <h6 className="color">Update Card Details</h6>
              <div className="br"></div>
              <form onSubmit={handleSubmit(onSubmit)} className="user_add_form">
                <div className="from_row row">
                  <div className="col-sm-6">
                    <h6>Card Holder Name</h6>
                    <div className="form_blk">
                      <input
                        defaultValue={
                          card_details && card_details?.card_holder_name
                        }
                        type="text"
                        className="input"
                        {...register("card_holder_name", {
                          required: "Card holder name is required.",
                          minLength: {
                            value: 2,
                            message:
                              "Card holder name should contain at least 2 letters.",
                          },
                        })}
                      />
                      <span className="validation-error">
                        {errors.card_holder_name?.message}
                      </span>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <h6>Card Number</h6>
                    <div className="form_blk stripe_wrap">
                      <CardElement
                        options={options}
                        onChange={handleCardDetailsChange}
                      />
                      {cardError && (
                        <p className="validation_error" role="alert">
                          {cardError}
                        </p>
                      )}
                      {invoiceUrl && (
                        <a
                          href={invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mini_color_text"
                        >
                          Complete the payment on Stripe
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="btn_blk text-center">
                  <SubmitButton
                    classes="site_btn block"
                    isFormProcessing={isFormProcessing}
                    button_text="Submit"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
